import { AfterViewInit, Component, DestroyRef, ElementRef, ViewChild, inject, signal } from '@angular/core';

/** Keeps navigation reachable when the workspace header and user footer reduce its height. */
@Component({
  selector: 'app-scroll-navigation',
  template: `
    @if (overflow()) {
      <div class="scroll-controls">
        <span>{{ below() ? 'More menu items below' : 'More menu items above' }}</span>
        <button type="button" aria-label="Scroll menu up" [disabled]="!above()" (click)="scroll(-1)">
          <span class="material-symbols-outlined" aria-hidden="true">expand_less</span>
        </button>
        <button type="button" aria-label="Scroll menu down" [disabled]="!below()" (click)="scroll(1)">
          <span class="material-symbols-outlined" aria-hidden="true">expand_more</span>
        </button>
      </div>
    }
    <div #viewport class="scroll-viewport" (scroll)="measure()">
      <div #content><ng-content /></div>
    </div>
  `,
  styles: [`
    :host { display:flex; flex-direction:column; min-height:0; overflow:hidden; }
    .scroll-viewport { flex:1; min-height:0; overflow-y:auto; overscroll-behavior:contain;
      scrollbar-gutter:stable; scrollbar-width:thin;
      scrollbar-color:var(--workspace-primary, #0ea5e9) transparent; }
    .scroll-controls { display:flex; align-items:center; gap:4px; flex-shrink:0; padding:0 2px 6px;
      color:var(--text-muted, #64748b); font-size:11px; }
    .scroll-controls > span { flex:1; }
    button { display:grid; place-items:center; width:32px; height:32px; padding:0; border:1px solid currentColor;
      border-radius:6px; background:transparent; color:var(--workspace-primary, #0ea5e9); cursor:pointer; }
    button:disabled { opacity:.35; cursor:default; }
    button:focus-visible { outline:2px solid var(--workspace-primary, #0ea5e9); outline-offset:2px; }
  `],
})
export class ScrollNavigation implements AfterViewInit {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);
  @ViewChild('viewport', { static: true }) private viewport!: ElementRef<HTMLElement>;
  @ViewChild('content', { static: true }) private content!: ElementRef<HTMLElement>;
  protected readonly overflow = signal(false);
  protected readonly above = signal(false);
  protected readonly below = signal(false);

  ngAfterViewInit(): void {
    if (typeof ResizeObserver === 'undefined') return;
    let frame = 0;
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => this.measure());
    };
    const observer = new ResizeObserver(schedule);
    observer.observe(this.host.nativeElement);
    observer.observe(this.viewport.nativeElement);
    observer.observe(this.content.nativeElement);
    schedule();
    this.destroyRef.onDestroy(() => { observer.disconnect(); cancelAnimationFrame(frame); });
  }

  protected measure(): void {
    const viewport = this.viewport.nativeElement;
    // Compare against the full host so adding controls cannot create a false overflow state.
    this.overflow.set(this.content.nativeElement.scrollHeight > this.host.nativeElement.clientHeight + 1);
    this.above.set(viewport.scrollTop > 1);
    this.below.set(viewport.scrollHeight - viewport.clientHeight - viewport.scrollTop > 1);
  }

  protected scroll(direction: number): void {
    const viewport = this.viewport.nativeElement;
    viewport.scrollBy({ top: direction * Math.max(80, viewport.clientHeight * .7), behavior: 'auto' });
  }
}
